/**
 * Deeplink helper utilities
 */
import getConfig from 'next/config';
import get from 'lodash/get';

import {toPascalCase} from '~/src/utilities/helpers/string';
import {TeaserTypes} from '~/src/utilities/constants/types';
import {getFeedUrl} from '~/src/utilities/helpers/feed';
import {IS_HTTP_PROTOCOL} from '~/src/utilities/constants/regex';
import {getCategoryBy} from '../feed/sitemap';

const {publicRuntimeConfig} = getConfig();

/**
 * Regex to match a deeplink command
 */
const deepLinkMatcher = new RegExp(publicRuntimeConfig.deeplinks.matcher, 'i');

/**
 * Transform a URLSearchParams object into a plain javascript object
 *
 * @param {Object} params URLSearchParams object containing parameters
 * @param {String[]} filter allowed search params
 */
const searchParamsToObject = (params, filter) => {
    const obj = {};
    for (const [key, value] of params) {
        if (!filter || filter.includes(key)) {
            obj[key] = value || true;
        }
    }

    return obj;
};

/**
 * Teaser data transformations for each deeplink command
 */
const teaserTransformers = {
    // CRASS transformations
    CmdCrass: (element, config) => ({
        type: 'ad',
        id: element.id,
        adUnitName: config.searchParams.get('adunit'),
        isCrassAd: true,
        forcePosition: config.searchParams.get('teaserpos')
    }),
    // External browser transformations
    CmdOpenexternalbrowser: (element, config) => ({
        ...element,
        type: 'links',
        content: {
            ...element.content,
            openInNewWindow: true,
            url: config.searchParams.get('url')
        }
    }),
    // Slider transformations
    CmdSlider: (element, config, sitemap) => {
        const categoryId = parseInt(config.searchParams.get('category'), 10);
        const category = getCategoryBy(sitemap, {categoryId});

        if (!category) {
            return {type: TeaserTypes.IGNORE};
        }

        return {
            type: TeaserTypes.SLIDER,
            id: element.id,
            url: getFeedUrl('category', {categoryId}),
            pointerCategoryID: categoryId,
            fetchAdditionalData: true,
            options: searchParamsToObject(config.searchParams, ['showLead', 'title', 'category', 'type', 'linkname'])
        };
    },
    CmdMostpopular: (element, config) => ({
        ...element,
        type: TeaserTypes.MOST_POPULAR,
        fetchAdditionalData: true,
        options: searchParamsToObject(config.searchParams, ['categoryId', 'sortBy', 'tenantId', 'limit', 'timeFrame'])
    }),
    // unsupported deeplink transformation (for error tracking / debug purpose)
    CmdNotSupported: (element, config) => ({
        ...element,
        type: 'links',
        content: {
            ...element.content,
            openInNewWindow: false,
            url: publicRuntimeConfig.deeplinks.fallbackUrl
        }
    })
};

/**
 * Check if an element is actually a deeplink command and transforms it.
 * No transformations are done if it does not match a deeplink
 *
 * @param {object} element feed element data to transform
 * @returns transformed data or the original element
 */
const transformDeeplink = (element, sitemap) => {
    const url = get(element, 'content.url', '');
    if (deepLinkMatcher.test(url)) {
        const config = new URL(url);
        const mapper = teaserTransformers[toPascalCase(config.pathname)] || teaserTransformers.CmdNotSupported;
        return mapper(element, config, sitemap);
    }

    return element;
};

/**
 * Url data transformations for all supported deeplinks
 */
const urlTransformers = {
    openexternalbrowser: (query) => {
        if (!IS_HTTP_PROTOCOL.test(query.url)) {
            return urlTransformers.notsupported();
        }

        return {
            type: 'redirect',
            redirectUrl: query.url,
            statusCode: 301
        };
    },
    openinternalbrowser: (query) => urlTransformers.openexternalbrowser(query),
    openoverlayexternalbrowser: (query) => urlTransformers.openexternalbrowser(query),
    openfullscreenwebview: (query) => urlTransformers.openexternalbrowser(query),
    jumptocategory: (query) => ({
        type: 'category',
        categoryId: parseInt(query.id, 10)
    }),
    notsupported: () => ({fallback: true})
};

/**
 * Create configuration from deeplink url
 */
const configFromCmd = (cmd = 'NotSupported', query = {}) => {
    const transformer = urlTransformers[cmd.toLowerCase()] || urlTransformers.notsupported;
    return transformer(query);
};

export {transformDeeplink, configFromCmd};
